/**
 * Ported from daisyUI v5 `fab` (Floating Action Button / Speed Dial).
 * Upstream: https://github.com/saadeghi/daisyui  (MIT License)
 *   packages/daisyui/src/components/fab.css
 * daisyUI ships a pure-CSS `:focus-within` + checkbox-hack speed dial; ry-fab
 * reimplements the behavior in clean JS (explicit open/close state, outside
 * click + Escape to close, ARIA wiring) so it composes like every other
 * ry-ui overlay. Attribution recorded in LICENSE-THIRD-PARTY.md.
 *
 * @component ry-fab
 * @category  overlay
 * @summary   Floating Action Button with an expanding speed-dial. A circular
 *            trigger button is fixed to a viewport corner; clicking it fans out
 *            a vertical stack of secondary actions. Click-outside or Escape
 *            collapses the dial. Each action is a `<ry-fab-action>` child (an
 *            interactive button). Use it for the one primary action on a screen
 *            plus a handful of related shortcuts — not as a general menu (use
 *            `<ry-dropdown>` for command menus that aren't viewport-pinned).
 *
 * @attribute icon        string?   default=plus       Icon name for the main trigger glyph (any registered `<ry-icon>` name). Rotates 45deg when open by default theme.
 * @attribute label       string?   default="Actions"  Accessible name for the trigger button (sets aria-label). Always provide one — the trigger is icon-only.
 * @attribute placement   string?   default=bottom-end bottom-end | bottom-start | top-end | top-start. Which viewport corner the FAB pins to, and which direction the dial expands from.
 * @attribute open        boolean   default=false      Reflects the expanded state. Present when the dial is fanned out, absent when collapsed. Mirrors `data-ry-state`.
 *
 * @property  state   'open' | 'closed'   Read-only mirror of `data-ry-state`. (There is no `.open` JS property — it would collide with the `open()` method. Use `open()` / `close()` / `toggle()`, or set/remove the `open` attribute.)
 *
 * @method    open()    Fan out the actions; sets aria-expanded="true", reveals actions, fires `ry:open`. No-op if already open.
 * @method    close()   Collapse the dial; sets aria-expanded="false", hides actions, fires `ry:close`. No-op if already closed.
 * @method    toggle()  open() if closed, close() if open.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of open() — trigger click, setting the `open` attribute, or programmatic open() — before aria-expanded or state change
 *        cancelable: preventDefault() keeps the dial collapsed; `ry:open` does not fire. If the open came from setting the `open` attribute, the attribute is removed again so it keeps mirroring state.
 *
 * @event ry:open
 *        detail: {}
 *        fires:  after open() transitions state to "open".
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of close() — trigger click, action click, outside click, Escape, removing the `open` attribute, or programmatic close() — before any state change
 *        cancelable: preventDefault() keeps the dial fanned out; `ry:close` does not fire. If the close came from removing the `open` attribute, the attribute is restored so it keeps mirroring state.
 *
 * @event ry:close
 *        detail: {}
 *        fires:  after close() transitions state to "closed" — via trigger click, action click, outside click, Escape, or programmatic close().

 *
 * @keyboard  Enter / Space on the trigger toggle the dial (native button keys via ry-button). Escape — collapse the dial and return focus to the trigger. Tab moves through the revealed actions while open.
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` on the host, mirrored by the boolean `open` attribute. Drives the CSS that reveals / hides the action stack and rotates the trigger icon.
 *
 * @content   `<ry-fab-action icon="…" label="…">` children become the dial actions. Each is rendered as a button row with the icon and (optionally) text content as its visible label. You may also drop a plain `<ry-button>` child — it is decorated as an action too.
 *
 * @dom
 *   ry-fab[data-ry-state="open|closed"][placement="bottom-end"]
 *     > ry-button.ry-fab__trigger   [data-ry-target="trigger"]  aria-haspopup="true" aria-expanded="true|false"
 *         > ry-icon (the `icon` glyph)
 *     > div.ry-fab__actions         [data-ry-target="actions"]   (the fan-out stack)
 *         > ry-fab-action.ry-fab__action   [data-ry-target="action"]  role="button" tabindex
 *         > ry-fab-action   (additional actions follow)
 *
 * @gotcha  The trigger is icon-only, so `label` is required for accessibility —
 *          it becomes the trigger's aria-label. If you omit it the trigger
 *          falls back to "Actions" and a console warning fires.
 *
 * @gotcha  Clicking an action closes the dial (like a menu item), AFTER the
 *          action's own click fires. There is no "stay open" mode. Listen for
 *          `ry:click` on the `<ry-fab-action>` (it forwards from ry-button) to
 *          run your handler; the collapse happens on the same tick.
 *
 * @gotcha  `position: fixed` pins the FAB to the VIEWPORT, not the nearest
 *          positioned ancestor — unless an ancestor establishes a containing
 *          block via `transform`/`filter`/`will-change`. If your FAB pins to
 *          the wrong box, an ancestor has one of those properties; move the FAB
 *          out of it or to `<body>` level.
 *
 * @gotcha  Actions are hidden via `visibility: hidden` + `opacity: 0` (not
 *          `display: none`) so they animate. They are NOT focusable or
 *          clickable while collapsed — happy-dom won't reflect the visibility,
 *          but real browsers and the `data-ry-state` gate enforce it.
 *
 * @example basic-speed-dial
 * ```html
 * <ry-fab label="Quick actions">
 *   <ry-fab-action icon="pencil" label="Edit"></ry-fab-action>
 *   <ry-fab-action icon="copy" label="Duplicate"></ry-fab-action>
 *   <ry-fab-action icon="trash" label="Delete"></ry-fab-action>
 * </ry-fab>
 * ```
 *
 * @example custom-icon
 * ```html
 * <ry-fab icon="share" label="Share">
 *   <ry-fab-action icon="user" label="To a person"></ry-fab-action>
 *   <ry-fab-action icon="mail" label="By email"></ry-fab-action>
 * </ry-fab>
 * ```
 *
 * @example placement-top-start
 * ```html
 * <ry-fab placement="top-start" label="Create">
 *   <ry-fab-action icon="file" label="New file"></ry-fab-action>
 *   <ry-fab-action icon="folder" label="New folder"></ry-fab-action>
 * </ry-fab>
 * ```
 *
 * @example labeled-actions
 * ```html
 * <ry-fab label="Compose">
 *   <ry-fab-action icon="pencil">Write a post</ry-fab-action>
 *   <ry-fab-action icon="image">Upload a photo</ry-fab-action>
 * </ry-fab>
 * ```
 *
 * @tutorial click-toggles-open
 * ```js
 * const el = mount(`
 *   <ry-fab label="Actions">
 *     <ry-fab-action icon="pencil" label="Edit"></ry-fab-action>
 *   </ry-fab>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * await expectEqual(el.state, 'closed', 'starts collapsed');
 * await expectEvent(el, 'ry:open', () => trigger.click());
 * await expectEqual(el.state, 'open', 'click fans out the dial');
 * await expectEvent(el, 'ry:close', () => trigger.click());
 * await expectEqual(el.state, 'closed', 'second click collapses');
 * ```
 *
 * @tutorial open-attribute-mirrors-state
 * ```js
 * // Wrong assumption: there is a boolean `.open` JS property. There is NOT —
 * // it would collide with the open() method. State reflects to the `open`
 * // ATTRIBUTE instead, and setting the attribute drives open()/close() so CSS
 * // and selectors can target [open] directly.
 * const el = mount(`
 *   <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 * `);
 * await expectEqual(el.hasAttribute('open'), false, 'no open attr while collapsed');
 * el.open();
 * await expectEqual(el.state, 'open');
 * await expectEqual(el.hasAttribute('open'), true, 'open attr present when fanned out');
 * el.setAttribute('open', '');
 * await expectEqual(el.state, 'open', 'setting the attribute is idempotent when already open');
 * el.close();
 * await expectEqual(el.state, 'closed');
 * await expectEqual(el.hasAttribute('open'), false, 'open attr removed when collapsed');
 * el.setAttribute('open', '');
 * await expectEqual(el.state, 'open', 'setting the open attribute fans it back out');
 * ```
 *
 * @tutorial action-click-closes
 * ```js
 * // Wrong assumption: actions stay open like a sticky toolbar. They do NOT —
 * // clicking an action collapses the dial after firing the action's click.
 * const el = mount(`
 *   <ry-fab label="A">
 *     <ry-fab-action id="act" icon="trash" label="Delete"></ry-fab-action>
 *   </ry-fab>
 * `);
 * el.open();
 * await expectEqual(el.state, 'open');
 * let actionFired = false;
 * el.querySelector('#act').addEventListener('ry:click', () => { actionFired = true; });
 * el.querySelector('#act').click();
 * await expectEqual(actionFired, true, 'the action click handler ran');
 * await expectEqual(el.state, 'closed', 'and the dial collapsed');
 * ```
 *
 * @tutorial escape-closes-and-returns-focus
 * ```js
 * // Wrong assumption: Escape closes but leaves focus floating. It returns
 * // focus to the trigger, matching ry-dropdown.
 * const el = mount(`
 *   <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'closed', 'escape collapses');
 * await expectEqual(document.activeElement, trigger, 'and returns focus to the trigger');
 * ```
 *
 * @tutorial outside-click-closes
 * ```js
 * const root = mount(`
 *   <div>
 *     <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 *     <span id="outside">elsewhere</span>
 *   </div>
 * `);
 * const fab = root.querySelector('ry-fab');
 * fab.open();
 * await expectEqual(fab.state, 'open');
 * root.querySelector('#outside').click();
 * await expectEqual(fab.state, 'closed', 'clicking outside collapses the dial');
 * ```
 *
 * @tutorial aria-haspopup-and-expanded
 * ```js
 * const el = mount(`
 *   <ry-fab label="Quick actions">
 *     <ry-fab-action icon="plus" label="Add"></ry-fab-action>
 *   </ry-fab>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * await expectAttr(trigger, 'aria-haspopup', 'true');
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * await expectAttr(trigger, 'aria-label', 'Quick actions');
 * el.open();
 * await expectAttr(trigger, 'aria-expanded', 'true');
 * el.close();
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * ```
 *
 * @tutorial open-and-close-are-idempotent
 * ```js
 * // Symmetric idempotency: repeated open() fires ry:open once; repeated
 * // close() fires ry:close once.
 * const el = mount(`
 *   <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 * `);
 * const opens = [];
 * const closes = [];
 * el.addEventListener('ry:open', () => opens.push(1));
 * el.addEventListener('ry:close', () => closes.push(1));
 * el.open(); el.open(); el.open();
 * await expectEqual(opens.length, 1, 'open() is idempotent');
 * el.close(); el.close();
 * await expectEqual(closes.length, 1, 'close() is idempotent');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the dial collapsed — trigger
 * // click or el.open(); ry:open is silent. Without the veto it fans out.
 * const el = mount(`
 *   <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 * `);
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.querySelector('[data-ry-target="trigger"]').click();
 * await expectEqual(el.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(el.hasAttribute('open'), false, 'open attr stays absent');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the dial fanned out — Escape,
 * // action click, or el.close(); ry:close is silent.
 * const el = mount(`
 *   <ry-fab label="A"><ry-fab-action icon="plus" label="Add"></ry-fab-action></ry-fab>
 * `);
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(el.hasAttribute('open'), true, 'open attr stays present');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-dropdown
 * @see ry-button
 * @see ry-icon
 */


import { RyElement } from '../core/ry-element.js';

export class RyFab extends RyElement {
  static get observedAttributes(): string[] {
    return ['open'];
  }

  #trigger: HTMLElement | null = null;
  #actions: HTMLElement | null = null;

  setup(): void {
    this.#buildMarkup();

    this.#trigger = this.$<HTMLElement>('[data-ry-target="trigger"]');
    this.#actions = this.$<HTMLElement>('[data-ry-target="actions"]');

    if (this.#trigger) {
      this.#trigger.setAttribute('aria-haspopup', 'true');
      this.#trigger.setAttribute('aria-expanded', 'false');
      this.on(this.#trigger, 'click', () => this.toggle());
    }

    // Each action collapses the dial on click (after its own click fires).
    this.$$('[data-ry-target="action"]').forEach((action) => {
      this.on(action, 'click', () => this.close());
    });

    // Outside-click collapses. The host contains both trigger and actions,
    // so the shared helper's contains() check is sufficient.
    this.onOutsideClick(() => this.close(), { skip: () => this.state !== 'open' });

    // Escape collapses + returns focus to the trigger.
    this.onEscape(
      () => {
        this.close();
        this.#trigger?.focus();
      },
      { skip: () => this.state !== 'open' },
    );

    // Initialize from the authored open attribute. Write the data attribute
    // explicitly so the CSS state gate is unambiguous even when the value
    // equals RyElement's "closed" default (the setter is a no-op in that case).
    const initial = this.hasAttribute('open') ? 'open' : 'closed';
    this.dataset.ryState = initial;
    if (initial === 'open') this.#trigger?.setAttribute('aria-expanded', 'true');
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name !== 'open') return;
    if (oldValue === newValue) return;
    // Only react after setup has run so the initial attribute parse doesn't
    // double-fire alongside setup()'s state initialization.
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (newValue === null) this.close();
    else this.open();
  }

  /**
   * Decorate authored markup. The trigger is created from the `icon` + `label`
   * attributes (an icon-only ry-button), and the actions are wrapped in a
   * stack container. Idempotent — re-running finds the existing scaffold.
   */
  #buildMarkup(): void {
    // Gather the action children authored on the host (ry-fab-action or a
    // plain ry-button that is NOT already the trigger).
    const actionEls = this.$$<HTMLElement>(
      'ry-fab-action, ry-button:not([data-ry-target="trigger"])',
    ).filter((el) => el.closest('[data-ry-target="actions"]') === null
      && !el.matches('[data-ry-target="trigger"]'));

    // Build the trigger if absent.
    if (!this.$('[data-ry-target="trigger"]')) {
      const label = this.getAttribute('label');
      if (!label) {
        // eslint-disable-next-line no-console
        console.warn('[ry-fab] missing label — the trigger is icon-only and needs an accessible name', this);
      }
      const iconName = this.getAttribute('icon') ?? 'plus';
      const trigger = document.createElement('ry-button');
      trigger.setAttribute('data-ry-target', 'trigger');
      trigger.classList.add('ry-fab__trigger', 'ry-button');
      trigger.setAttribute('icon', '');
      trigger.setAttribute('circular', '');
      trigger.setAttribute('variant', 'primary');
      trigger.setAttribute('aria-label', label ?? 'Actions');
      trigger.innerHTML = `<ry-icon name="${iconName}"></ry-icon>`;
      this.prepend(trigger);
    }

    // Build the actions container if absent, and move actions into it.
    if (!this.$('[data-ry-target="actions"]')) {
      const container = document.createElement('div');
      container.setAttribute('data-ry-target', 'actions');
      container.classList.add('ry-fab__actions');
      actionEls.forEach((action) => {
        action.setAttribute('data-ry-target', 'action');
        action.classList.add('ry-fab__action');
        container.appendChild(action);
      });
      this.appendChild(container);
    }
  }

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) {
      // Vetoed. If the request came via the reflected `open` attribute,
      // un-reflect it so attribute and state keep mirroring each other.
      if (this.hasAttribute('open')) this.removeAttribute('open');
      return;
    }
    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.state = 'open';
    if (!this.hasAttribute('open')) this.setAttribute('open', '');
    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) {
      // Vetoed. If the request came via removing the `open` attribute,
      // restore it so attribute and state keep mirroring each other.
      if (!this.hasAttribute('open')) this.setAttribute('open', '');
      return;
    }
    this.#trigger?.setAttribute('aria-expanded', 'false');

    this.state = 'closed';
    if (this.hasAttribute('open')) this.removeAttribute('open');
    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') this.close();
    else this.open();
  }
}

customElements.define('ry-fab', RyFab);

/**
 * `<ry-fab-action>` — a single action row inside a `<ry-fab>`. It behaves like
 * an icon button: it renders its `icon` glyph and forwards a `ry:click` event
 * on activation. Authored as `<ry-fab-action icon="…" label="…">` or with text
 * children for an inline label.
 *
 * @component ry-fab-action
 * @category  overlay
 * @summary   One action inside a `<ry-fab>` speed-dial. Renders an icon plus an
 *            optional text label and fires `ry:click` when activated.
 *
 * @attribute icon    string?   Icon name for the action glyph.
 * @attribute label   string?   Accessible name (aria-label). Use when there is no visible text child.
 *
 * @event ry:click
 *        detail: { originalEvent: MouseEvent }
 *        fires:  on genuine activation (mouse, Enter, Space).
 *
 * @gotcha  Outside a `<ry-fab>`, `<ry-fab-action>` still renders its icon/label
 *          and fires `ry:click`, but nothing collapses a dial — the
 *          collapse-on-click behavior is wired by the parent `<ry-fab>`.
 */
export class RyFabAction extends RyElement {
  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    const label = this.getAttribute('label');
    if (label && !this.hasAttribute('aria-label')) this.setAttribute('aria-label', label);

    // Render the glyph + (optional) text label once.
    if (!this.$('.ry-fab__action-icon')) {
      const iconName = this.getAttribute('icon');
      const text = this.textContent?.trim() ?? '';
      const parts: string[] = [];
      if (iconName) parts.push(`<ry-icon class="ry-fab__action-icon" name="${iconName}"></ry-icon>`);
      if (text) parts.push(`<span class="ry-fab__action-label">${text}</span>`);
      // Always include an icon hook so the styling selector matches even when
      // only text is provided.
      if (parts.length) this.innerHTML = parts.join('');
    }

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #handleClick = (event: MouseEvent): void => {
    // Re-entrancy guard: emit() dispatches ry:click, but a raw DOM click also
    // lands here. Only forward genuine activations once.
    this.emit('click', { originalEvent: event });
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.click();
  };
}

customElements.define('ry-fab-action', RyFabAction);
